import toggle from '@/ui/toggle';
import { eventBus } from '@weareduet/plugins';
import { loadGsap, refreshScroll } from '@/utils/gsap';
import { stopScroll, startScroll } from '@/utils/lenis';

function Header({ node, name }) {
	if (!node) return () => {};
	/*
	 *		Variables
	 */

	const headerTheme = node.dataset.theme ?? '';

	// Show/hide is scrubbed to scroll via ScrollTrigger (see the async setup
	// below): the header translate tracks scroll movement 1:1. `fixed` headers
	// opt out entirely. `frozen` pauses the scrub while the nav is open or the
	// hero intro is running; `showHeader` pins it back into view.
	const enableHide = headerTheme !== 'fixed' && headerTheme !== 'relative';
	let frozen = false;
	let showHeader = () => {};
	// Freeze/unfreeze the scroll-scrubbed show/hide. Assigned real bodies once
	// the scrub exists (see the async setup below); `unfreezeScrub` is settle-
	// aware — it doesn't resume the instant it's called, because a layout change
	// that shifts the scroll offset (an accordion collapse) keeps producing
	// residual scroll movement for a few frames afterwards. `cancelSettle` tears
	// down any in-flight settle loop on destroy.
	let freezeScrub = () => {
		frozen = true;
	};
	let unfreezeScrub = () => {
		frozen = false;
	};
	let cancelSettle = () => {};

	let windowTop = 0;
	const lock = {
		capture() {
			windowTop = window.scrollY;
			// Pause Lenis (mobile) before the body lock so syncTouch doesn't keep
			// building a scroll target behind the fixed body and lurch on release.
			stopScroll();
			document.body.style.position = 'fixed';
			document.body.style.top = `${windowTop * -1}px`;
			document.body.style.overflow = 'hidden';
			document.body.style.width = '100vw';

			// Keep the header visible while the nav is open and freeze the
			// direction-driven hide so it can't slide away underneath the panel.
			// Cancel any in-flight accordion settle so it can't clear `frozen`
			// out from under the open nav.
			cancelSettle();
			frozen = true;
			showHeader();
		},
		release() {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.overflow = '';
			document.body.style.width = '';
			if (windowTop) window.scroll(0, windowTop);
			startScroll();

			frozen = false;
		},
	};

	const nav = toggle({
		button: node.querySelector('.burger'),
		name,
		activeClass: 'is-active',
	});

	/*
	 *		Functions
	 */

	function clickWhenOpenHandle({ target }) {
		if (target.id === 'header' || target.closest('#header')) return; // Continue if clicking within #Header
		nav.close();
	}

	function handleOpen({ target }) {
		node.classList.add('nav-open');
		target.classList.add('is-open');
		lock.capture();
		document.addEventListener('click', clickWhenOpenHandle);
	}

	function handleClose({ target }) {
		node.classList.remove('nav-open');
		target.classList.remove('is-open');
		lock.release();
		document.removeEventListener('click', clickWhenOpenHandle);
	}

	nav.on(`open:${name}`, handleOpen);
	nav.on(`close:${name}`, handleClose);

	// Main Mobile Menu END
	function onKeyDown(event) {
		const { key } = event;
		if (key === 'Escape') {
			if (nav.isOpen) {
				nav.close();
			}
		}
	}

	/*
	 *		ScrollTrigger — show/hide bound to scroll direction, plus per-block theming
	 */
	const scrollTriggers = [];
	// Detaches the dynamic block-colour listener (wired up once blocks resolve).
	let detachHeaderColor = () => {};
	// Re-measures which block sits under the header (wired up once blocks
	// resolve). Called after every ScrollTrigger refresh, since a refresh can
	// move the scroll position or re-length a pin without a scroll event.
	let reseedHeaderColor = () => {};
	// Detaches the post-refresh re-baseline (wired up in the async setup below).
	let detachScrollRefresh = () => {};

	// Blocks loaded later via Sprig/HTMX (e.g. a dynamicInclude skeleton that
	// swaps in its real content on `load` — the homepage's latestNewsBlock)
	// aren't in the DOM yet when the initial `[data-block]` query below runs,
	// so they'd never get a colour or a ScrollTrigger. Listen for swaps from
	// the very start — a swap can land before or after the async setup below
	// finishes loading GSAP — and queue any that arrive early.
	let registerSwappedBlocks = null;
	const pendingSwapTargets = [];
	const handleBlockSwap = e => {
		const swapTarget = e.detail?.target ?? e.target;
		if (!swapTarget) return;
		if (registerSwappedBlocks) registerSwappedBlocks(swapTarget);
		else pendingSwapTargets.push(swapTarget);
	};
	if (typeof htmx !== 'undefined') {
		htmx.on('htmx:afterSwap', handleBlockSwap);
	}

	// The show/hide scrub's range is `0 → max scroll`, measured at refresh time,
	// so any DOM height change leaves it stale. HTMX "load more" (WorkGrid,
	// NewsMasonry) appends cards without touching `[data-block]`, so the swap
	// handler above never fires a refresh — and once the scroll passes the old
	// max, the scrub's `onUpdate` stops firing entirely: the header sticks
	// wherever it was and scrolling up no longer brings it back.
	//
	// Watching the document box catches every height change, not just swaps —
	// images settling, a Masonry relayout, an accordion opening. `refreshScroll`
	// batches the calls, and `onScrollRefresh` below re-baselines the scrub after
	// each recompute so the shift can't read as a scroll delta.
	let lastDocHeight = document.documentElement.scrollHeight;
	const heightObserver = new ResizeObserver(() => {
		// The nav lock puts `body` in `position: fixed`, collapsing the document
		// to one screen; refreshing then would measure every trigger against a
		// page that isn't there. Releasing the lock fires this observer again.
		if (nav.isOpen) return;
		const height = document.documentElement.scrollHeight;
		if (Math.abs(height - lastDocHeight) < 2) return;
		lastDocHeight = height;
		refreshScroll();
	});
	heightObserver.observe(document.documentElement);

	(async () => {
		try {
			const { gsap, ScrollTrigger } = await loadGsap();

			/*
			 *	Show/hide — scrubbed to scroll. `hidden` is the px currently tucked
			 *	away, accumulated from the per-frame scroll delta and clamped between
			 *	0 (fully shown) and the reveal distance (fully hidden). Scrolling down
			 *	tucks the header up, scrolling up brings it back, 1:1 with scroll
			 *	distance. GSAP owns `transform`; CSS only transitions colour + opacity.
			 */
			if (enableHide) {
				// quickSetter writes yPercent instantly each frame — no tween, no
				// ease — so the header tracks the scroll position exactly 1:1.
				const setY = gsap.quickSetter(node, 'yPercent');

				let hidden = 0;
				let lastScroll = window.scrollY;
				// Read live so a viewport/breakpoint resize keeps the 1:1 mapping —
				// reveal distance = header height, i.e. scroll 1px → header moves 1px.
				const revealDistance = () => node.offsetHeight || 1;
				const render = () => setY((hidden / revealDistance()) * -100);

				// Nav-open pins the header in view; reset the accumulator so it
				// resumes from fully-shown when the scrub takes over again.
				showHeader = () => {
					hidden = 0;
					render();
				};

				const scrubTrigger = ScrollTrigger.create({
					start: 'top top',
					end: 'max',
					onUpdate(self) {
						if (frozen) return;
						const y = self.scroll();
						const delta = y - lastScroll;
						lastScroll = y;
						// Force fully shown at the very top.
						if (y <= 0) {
							hidden = 0;
						} else {
							hidden = Math.min(revealDistance(), Math.max(0, hidden + delta));
						}
						render();
					},
				});
				scrollTriggers.push(scrubTrigger);

				// Settle-aware freeze. A transient layout change (an accordion
				// panel collapsing) shrinks the document; the browser clamps
				// scrollY and Lenis re-emits `scroll`, and that residual movement
				// keeps arriving for a few frames AFTER the collapse's
				// transitionend — past the point we'd naively unfreeze. Resuming
				// the scrub then reads the leftover scroll-up as a reveal.
				//
				// So unfreezing runs a short settle loop instead: it holds the
				// scrub frozen and re-baselines `lastScroll` to the live position
				// every frame (so no delta can accumulate) until the scroll has
				// been stable for a few consecutive frames — or a safety cap — and
				// only then resumes, with a zero delta and the header exactly
				// where it was.
				let settleRaf = null;
				cancelSettle = () => {
					if (settleRaf) cancelAnimationFrame(settleRaf);
					settleRaf = null;
				};

				freezeScrub = () => {
					cancelSettle();
					frozen = true;
				};

				unfreezeScrub = () => {
					cancelSettle();
					let prev = scrubTrigger.scroll();
					let stableFrames = 0;
					let elapsed = 0;
					// Wait out any lagging clamp/refresh before we're allowed to
					// resume, require a run of quiet frames, and cap the wait so a
					// continuous scroll can't keep the header pinned indefinitely.
					const MIN_FRAMES = 6;
					const STABLE_NEEDED = 3;
					const MAX_FRAMES = 48;
					const step = () => {
						const y = scrubTrigger.scroll();
						lastScroll = y;
						stableFrames = Math.abs(y - prev) < 1 ? stableFrames + 1 : 0;
						prev = y;
						elapsed += 1;
						const settled =
							elapsed >= MIN_FRAMES && stableFrames >= STABLE_NEEDED;
						if (settled || elapsed >= MAX_FRAMES) {
							lastScroll = scrubTrigger.scroll();
							frozen = false;
							settleRaf = null;
							return;
						}
						settleRaf = requestAnimationFrame(step);
					};
					settleRaf = requestAnimationFrame(step);
				};
			}

			// After every ScrollTrigger recompute: re-baseline the scrub through
			// the same settle loop the accordion freeze uses. A refresh re-measures
			// `end: 'max'` (the point of it) but can also shift or clamp the scroll
			// position, and the scrub would read that as a scroll-up and reveal.
			//
			// Skipped while something already owns the header (nav open, hero intro,
			// an in-flight settle) — that owner's own unfreeze re-baselines.
			//
			// Also re-notes the height we've now measured at, so the pin-spacer
			// resize the refresh itself causes can't bounce the observer above into
			// a second refresh.
			const onScrollRefresh = () => {
				lastDocHeight = document.documentElement.scrollHeight;
				reseedHeaderColor();
				if (!enableHide || frozen) return;
				freezeScrub();
				unfreezeScrub();
			};
			ScrollTrigger.addEventListener('refresh', onScrollRefresh);
			detachScrollRefresh = () =>
				ScrollTrigger.removeEventListener('refresh', onScrollRefresh);

			// Only top-level section blocks drive the header colour. A nested
			// `data-block` (the Truth/Dare carousel overlays its section at
			// `inset-0`) shares its parent's top, so both would always resolve as
			// "under the header" together and the inner one — invisible for most of
			// the section — would win, painting the header its colour (white)
			// instead of the section's (pink). Excluding descendants keeps the
			// header tracking real sections.
			const isTopLevelBlock = block =>
				!block.parentElement?.closest('[data-block]');

			const blocks = [...document.querySelectorAll('[data-block]')].filter(
				isTopLevelBlock
			);

			const resolveBackground = el => {
				let current = el;
				while (current) {
					const bg = window.getComputedStyle(current).backgroundColor;
					if (bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
					if (current === document.documentElement) break;
					current = current.parentElement;
				}
				return 'rgb(255, 255, 255)';
			};

			const blockColors = blocks.map(block => ({
				backgroundColor: resolveBackground(block),
				color: window.getComputedStyle(block).color,
			}));

			// Colour is driven solely by each block's TOP crossing the viewport top:
			// the header matches whichever block currently sits under it — the LAST
			// (lowest-on-page) block whose top has reached/crossed that line. We
			// avoid any `bottom`-based switch (e.g. "apply the next block's colour
			// when this one's bottom passes") because a pinned block reports its
			// natural, un-pinned height — its bottom sits ~one screen down, not
			// after the full pinned scroll — so that switch fires far too early.
			//
			// That question is answered by MEASURING each block live, rather than by
			// a per-block ScrollTrigger holding a cached start position, because a
			// block that pins itself (Truth/Dare, TextCarousel) can't be measured
			// that way: while pinned the section is `position: fixed`, and once its
			// pin ends ScrollTrigger re-parks it at the BOTTOM of its pin-spacer. A
			// trigger measuring that element therefore resolved to the END of the pin
			// (~22,000px down the Approach page) instead of its start — so the
			// section's colour only applied for its last screenful, and scrolling
			// back up cleared the header's colour entirely for the whole pinned
			// stretch (no Truth pink, no Dare black on the way back).
			//
			// A live measurement has no such blind spot — provided it's taken from
			// the block's OUTER box. For a pinned block that's ScrollTrigger's
			// pin-spacer: a plain static element that starts where the pin starts
			// and is as tall as the whole pinned stretch, so its top crosses the
			// viewport top exactly once, at the right place, whatever state the pin
			// itself is in. Measuring the pinned element directly would work for
			// most of the scroll (it reads 0 while fixed) but flickers on the frames
			// either side of the pin engaging/releasing, as ScrollTrigger moves it
			// between `fixed` and its parked position in the spacer.
			//
			// The 1px tolerance covers sub-pixel layout at the very top so the first
			// block paints immediately.
			let activeIndex = -1;
			const outerBox = block => {
				const parent = block.parentElement;
				return parent?.classList.contains('pin-spacer') ? parent : block;
			};
			const blockTop = index =>
				outerBox(blocks[index]).getBoundingClientRect().top;

			// Full re-measure. Used to seed, and whenever the block list changes or
			// the layout is recomputed under us.
			const scanActiveBlock = () => {
				let index = -1;
				blocks.forEach((block, i) => {
					if (outerBox(block).getBoundingClientRect().top < 1) index = i;
				});
				return index;
			};

			const renderHeaderColor = () => {
				const colors = blockColors[activeIndex];
				if (!colors) {
					node.style.removeProperty('background-color');
					node.style.removeProperty('color');
					return;
				}
				node.style.backgroundColor = colors.backgroundColor;
				node.style.color = colors.color;
			};

			// Per-frame update. Blocks are held in document order, so only the
			// boundary needs measuring: walk forward while the NEXT block's top has
			// crossed, back while the CURRENT one's no longer has. That's a couple of
			// rect reads per scroll frame rather than one per block — and because
			// both are `while` loops it self-corrects after a jump (scroll restore,
			// a pin rebuild) that skips several blocks at once.
			const syncActiveBlock = () => {
				let index = activeIndex;
				while (index + 1 < blocks.length && blockTop(index + 1) < 1) index += 1;
				while (index >= 0 && blockTop(index) >= 1) index -= 1;
				if (index === activeIndex) return;
				activeIndex = index;
				renderHeaderColor();
			};

			scrollTriggers.push(
				ScrollTrigger.create({
					start: 'top top',
					end: 'max',
					onUpdate: syncActiveBlock,
				})
			);

			reseedHeaderColor = () => {
				const index = scanActiveBlock();
				if (index === activeIndex) return;
				activeIndex = index;
				renderHeaderColor();
			};

			// Seed the initial colour — nothing has scrolled yet, and on a
			// scroll-restored load this paints the correct mid-page colour rather
			// than always assuming the first block.
			activeIndex = scanActiveBlock();
			renderHeaderColor();

			// Registers a block that lands after this initial pass — a Sprig/HTMX
			// swap replacing a dynamicInclude's skeleton with its real content.
			// Folded into the same shared arrays so it takes part in the "lowest
			// block under the header wins" resolution exactly like an initial
			// block. The new content changes the page height, shifting every later
			// trigger's position, so everything needs to recompute —
			// `refreshScroll()` is the shared, batched entry point for that.
			registerSwappedBlocks = swapTarget => {
				const candidates = swapTarget.matches?.('[data-block]')
					? [swapTarget]
					: [...swapTarget.querySelectorAll('[data-block]')];
				const newBlocks = candidates.filter(
					block => isTopLevelBlock(block) && !blocks.includes(block)
				);
				if (!newBlocks.length) return;

				// Re-read the page in DOCUMENT order — a swap can land anywhere, and
				// `syncActiveBlock`'s walk depends on that order. Colours already
				// resolved (including any patched by `header:color` below) carry
				// across, since they're keyed off the element, not its position.
				const known = new Map(
					blocks.map((block, i) => [block, blockColors[i]])
				);
				const ordered = [...document.querySelectorAll('[data-block]')].filter(
					isTopLevelBlock
				);
				blocks.length = 0;
				blockColors.length = 0;
				ordered.forEach(block => {
					blocks.push(block);
					blockColors.push(
						known.get(block) ?? {
							backgroundColor: resolveBackground(block),
							color: window.getComputedStyle(block).color,
						}
					);
				});

				// Indices have shifted — re-measure rather than adjust by hand.
				activeIndex = scanActiveBlock();
				renderHeaderColor();
				refreshScroll();
			};
			pendingSwapTargets.splice(0).forEach(registerSwappedBlocks);

			// Some blocks change background mid-scroll (the Truth section fades
			// pink → white during its pin), which the load-time snapshot above can't
			// see. They emit `header:color` with their block id and the new colour;
			// patch the cached entry and repaint so an active block updates live.
			const onHeaderColor = ({ id, backgroundColor, color }) => {
				const i = blocks.findIndex(block => block.id === id);
				if (i === -1) return;
				blockColors[i] = {
					...blockColors[i],
					...(backgroundColor ? { backgroundColor } : {}),
					...(color ? { color } : {}),
				};
				renderHeaderColor();
			};
			eventBus.on('header:color', onHeaderColor);
			detachHeaderColor = () => eventBus.off('header:color', onHeaderColor);
		} catch (error) {
			console.error('Header: Failed to load ScrollTrigger', error);
		}
	})();

	/*
	 *		Hero intro — freeze/unfreeze headroom around the page-load carousel sequence
	 *		and while the scroll-pinned section is active.
	 */
	const onHeroIntroStart = () => {
		frozen = true;
	};
	const onHeroIntroEnd = () => {
		frozen = false;
	};

	eventBus.on('hero:intro:start', onHeroIntroStart);
	eventBus.on('hero:intro:end', onHeroIntroEnd);

	// Generic freeze for transient layout changes that move the scroll offset
	// (an accordion panel collapsing shrinks the document, the browser clamps
	// scrollY, and the scrub would read that as a scroll-up and reveal). Pause
	// the scrub for the duration, then re-baseline so it resumes from where it
	// was rather than snapping to absorb the shift in a single frame.
	const onHeaderFreeze = () => freezeScrub();
	const onHeaderUnfreeze = () => unfreezeScrub();
	eventBus.on('header:freeze', onHeaderFreeze);
	eventBus.on('header:unfreeze', onHeaderUnfreeze);

	/*
	 *		Init
	 */
	nav.init();
	document.addEventListener('keydown', onKeyDown);

	return () => {
		document.removeEventListener('click', clickWhenOpenHandle);
		document.removeEventListener('keydown', onKeyDown);

		nav.off(`open:${name}`, handleOpen);
		nav.off(`close:${name}`, handleClose);

		if (nav.isOpen) {
			lock.release();
		}

		cancelSettle();
		scrollTriggers.forEach(t => t.kill());
		heightObserver.disconnect();
		detachHeaderColor();
		detachScrollRefresh();
		if (typeof htmx !== 'undefined') {
			htmx.off('htmx:afterSwap', handleBlockSwap);
		}

		eventBus.off('hero:intro:start', onHeroIntroStart);
		eventBus.off('hero:intro:end', onHeroIntroEnd);
		eventBus.off('header:freeze', onHeaderFreeze);
		eventBus.off('header:unfreeze', onHeaderUnfreeze);

		nav.destroy();
	};
}

export default Header;
